import { z } from '@dcp/shared'

/**
 * Validation schema for the batch "Add Projection" form (DCP-255).
 *
 * Extracted from ProjectionBatchFormDialog so it can be unit-tested directly
 * (and so the component file only exports a component — react-refresh). The
 * dialog is CREATE-only, so every listed field is required.
 */

/**
 * Schema for a per-facility amount entry, used when multiple facilities are
 * selected. Amount is optional at the field level; the required constraint is
 * enforced by {@link projectionRowSchema}'s superRefine in multi-facility mode.
 */
const facilityAmountSchema = z.object({
  facility_id: z.string(),
  // DCP-250 AC#4: negatives are valid (outflow) — no positivity constraint.
  // DCP-224: invalid text arrives as NaN — surface a clear message. optional()
  // allows undefined (empty input); superRefine below enforces required.
  amount: z.number({ invalid_type_error: 'Enter a valid amount' }).optional(),
})

/**
 * Validation schema for a single projection row. Supports multi-facility
 * selection with per-facility amounts.
 *
 * Amount-required is conditional on facility_ids.length:
 *   - Single/no facility  → row.amount must be defined.
 *   - Multi-facility (>1) → EVERY selected facility must have a defined amount.
 * Enforced via superRefine because the constraint crosses fields.
 */
export const projectionRowSchema = z
  .object({
    // DCP-255: facility is required for every projection row.
    facility_ids: z.array(z.string()).min(1, 'Facility is required'),
    facility_amounts: z.array(facilityAmountSchema).default([]),
    category_id: z.string().min(1, 'Category is required'),
    date: z.string().min(1, 'Date is required'),
    // DCP-255: description is required for every projection row.
    description: z.string().trim().min(1, 'Description is required'),
    // DCP-250 AC#4: negatives are valid (outflow) — no positivity constraint.
    // optional() allows undefined (empty input); superRefine enforces required
    // for single-facility mode.
    amount: z.number({ invalid_type_error: 'Enter a valid amount' }).optional(),
    priority: z.boolean().default(false),
  })
  .superRefine((row, ctx) => {
    // DCP-255: amount required — enforced conditionally based on facility mode.
    // 0 and negative values are valid (DCP-250); only undefined (empty) fails.
    if (row.facility_ids.length > 1) {
      // Multi-facility mode: validate over the SELECTED facilities (the source of
      // truth), not the existing facility_amounts entries. This way a
      // facility_amounts array that is empty or shorter than facility_ids still
      // fails validation — otherwise a missing per-facility amount would slip
      // through and onSubmit could silently drop that facility (Presto #1).
      row.facility_ids.forEach((facilityId) => {
        const idx = row.facility_amounts.findIndex((fa) => fa.facility_id === facilityId)
        if (idx === -1 || row.facility_amounts[idx].amount === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Amount is required',
            // Point at the specific per-facility amount when its row exists; when
            // the entry is missing entirely, anchor at the array so submission is
            // still blocked (the UI renders one row per facility_amounts entry).
            path: idx === -1 ? ['facility_amounts'] : ['facility_amounts', idx, 'amount'],
          })
        }
      })
    } else {
      // Single / no facility mode: validate the row-level amount field.
      if (row.amount === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Amount is required',
          path: ['amount'],
        })
      }
    }
  })

/** Form schema: an array of projection rows (at least one). */
export const projectionsFormSchema = z.object({
  projections: z.array(projectionRowSchema).min(1, 'Add at least one projection'),
})

export type ProjectionsFormData = z.infer<typeof projectionsFormSchema>
